import React, { useState } from 'react';
import { arrayOf, bool, func, object, shape } from 'prop-types';
import { useDispatch } from 'react-redux';
import { uniqueId } from 'lodash';
import ActionBar from '../ActionBar';
import ActionBarItem from '../ActionBarItem';
import SensorsTable from '../SensorsTable';

import { enqueueSnackbar } from '../../actions/applicationMessageActions';
import ModifyRuleSensorModal from '../../containers/Modals/ModifyRuleSensorModal';
import { TabContent } from '../TabPanel';
import { TEMP_ID } from '../../helpers/sensors';

const BLANK_SENSOR = Object.freeze({
    equipmentTypeId:   null,
    equipmentTypeName: null,
    id:                null,
    label:             null,
    measurement:       null,
    multiSensorOption: null,
    ruleId:            null,
    ruleName:          null,
    sensorSourcing:    {
        associatedSystems: false,
        equipmentTarget:   false,
        siblingEquipment:  false,
        systemEquipment:   false,
        systemTarget:      false
    },
    sensorAggregateDuration: 0,
    sensorAggregateType:     'None',
    sensorTypeId:            null,
    sensorTypeName:          null,
    sourceType:              null,
    systemTypeId:            null,
    systemTypeName:          null
});

const RuleSensorsFieldset = ({ disabled, onPatch, readOnly, values }) => {
    const dispatch = useDispatch();
    const [selectedSensor, setSelectedSensor] = useState();

    function handleConfirm(modifiedSensor) {
        const sensorIndex = values.sensors.findIndex(
            ({ id }) => modifiedSensor.id === id
        );
        let action = '';
        let updatedSensors;
        if (sensorIndex === -1) {
            updatedSensors = [
                ...values.sensors,
                {
                    ...modifiedSensor,
                    id: uniqueId(TEMP_ID)
                }
            ];
            action = 'Sensor created!';
        } else {
            updatedSensors = [...values.sensors];
            updatedSensors.splice(sensorIndex, 1, modifiedSensor);
            action = 'Updated!';
        }
        onPatch({
            sensors: updatedSensors
        });
        dispatch(
            enqueueSnackbar(`${action} Don't forget to save the rule for your changes to take effect!`, 'success')
        );
        setSelectedSensor();
    }

    function handleDeleteSensor(rowId) {
        const sensors = [...values.sensors];
        sensors.splice(rowId, 1);
        onPatch(
            {
                sensors
            },
            { validateForm: true }
        );
        dispatch(
            enqueueSnackbar(`Sensor deleted! Don't forget to save the rule for your changes to take effect!`, 'success')
        );
    }

    function handleNewSensor() {
        if (!values.target) {
            dispatch(
                enqueueSnackbar(`A Rule Target Filter must be selected before a sensor can be added.`, 'warning')
            );
            return;
        }
        setSelectedSensor({ ...BLANK_SENSOR });
    }

    function handleModifySensorClose() {
        setSelectedSensor(undefined);
    }

    function handleSelectSensor(rowId) {
        setSelectedSensor(values.sensors[rowId]);
    }

    return (
        <TabContent>
            <ActionBar>
                <ActionBarItem
                    disabled={disabled || readOnly}
                    onClick={handleNewSensor}
                    type="add"
                >
                    New Sensor Filter
                </ActionBarItem>
            </ActionBar>
            <SensorsTable
                disabled={disabled}
                onNewSensor={handleNewSensor}
                onDeleteSensor={handleDeleteSensor}
                onSelectSensor={handleSelectSensor}
                readOnly={readOnly}
                sensors={values.sensors}
            />
            <ModifyRuleSensorModal
                defaultSensor={BLANK_SENSOR}
                onClose={handleModifySensorClose}
                onConfirm={handleConfirm}
                open={Boolean(selectedSensor)}
                ruleTarget={values.target}
                sensor={selectedSensor}
            />
        </TabContent>
    );
};

RuleSensorsFieldset.defaultProps = {
    disabled: false,
    onPatch:  () => {},
    rule:     {
        sensors: []
    },
    readOnly: false
};

RuleSensorsFieldset.props = {
    disabled: bool,
    onPatch:  func,
    rule:     shape({
        sensors: arrayOf(object)
    }),
    readOnly: bool
};

export default RuleSensorsFieldset;
